import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../utils/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { CheckCircle2, XCircle, Clock, Paperclip, Save, FileDown } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STAGE_ORDER = ['accountant', 'manager', 'president'];
const STAGE_LABELS = { accountant: 'المحاسب', manager: 'المدير التنفيذي', president: 'الرئيس' };
const REQUEST_TYPES = ['عام', 'سلفة', 'مصروف', 'استرداد'];

const STATUS_LABELS = {
  pending_accountant: 'بانتظار المحاسب',
  pending_manager: 'بانتظار المدير التنفيذي',
  pending_president: 'بانتظار الرئيس',
  approved: 'معتمد',
  rejected: 'مرفوض',
};

const STATUS_COLORS = {
  pending_accountant: 'bg-amber-100 text-amber-800',
  pending_manager: 'bg-amber-100 text-amber-800',
  pending_president: 'bg-amber-100 text-amber-800',
  approved: 'bg-emerald-100 text-emerald-800',
  rejected: 'bg-red-100 text-red-800',
};

export default function FinancialApprovals() {
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') === 'settings' ? 'settings' : 'requests';
  const approvedOnly = searchParams.get('status') === 'approved';

  const [employees, setEmployees] = useState([]);
  const [settings, setSettings] = useState({
    accountant_enabled: true,
    manager_enabled: true,
    president_enabled: true,
    accountant_employee_code: '',
    manager_employee_code: '',
    president_employee_code: '',
    accountant_allowed_types: [...REQUEST_TYPES],
    manager_allowed_types: [...REQUEST_TYPES],
    president_allowed_types: [...REQUEST_TYPES],
  });
  const [requests, setRequests] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [stageSignatures, setStageSignatures] = useState({ accountant: null, manager: null, president: null });
  const [uploadingStage, setUploadingStage] = useState(null);

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally run once on mount
  }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [empRes, settingsRes, requestsRes] = await Promise.all([
        api.get('/hr/employees'),
        api.get('/financial-approvals/settings'),
        api.get('/financial-requests'),
      ]);
      setEmployees(empRes.data || []);
      setSettings((prev) => ({ ...prev, ...settingsRes.data }));
      setRequests(requestsRes.data || []);
      loadStageSignatures(settingsRes.data);
    } catch (error) {
      toast.error('تعذر تحميل بيانات الموافقات المالية');
    } finally {
      setLoading(false);
    }
  };

  const loadStageSignatures = async (currentSettings) => {
    const results = {};
    await Promise.all(
      STAGE_ORDER.map(async (stage) => {
        const code = currentSettings[`${stage}_employee_code`];
        if (!code) return;
        try {
          const res = await api.get(`/financial-approvals/signature/${code}`);
          results[stage] = res.data?.signature_base64 || null;
        } catch (error) {
          results[stage] = null; // لا يوجد توقيع مرفوع بعد لهذا الموظف
        }
      })
    );
    setStageSignatures((prev) => ({ ...prev, ...results }));
  };

  const handleSignatureUpload = async (stage, file) => {
    const employeeCode = settings[`${stage}_employee_code`];
    if (!employeeCode) {
      toast.error('اختر الموظف المسؤول عن هذه المرحلة أولًا ثم احفظ الإعدادات');
      return;
    }
    if (file.size > 500 * 1024) {
      toast.error('حجم صورة التوقيع كبير جدًا (الحد الأقصى 500 كيلوبايت)');
      return;
    }
    setUploadingStage(stage);
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        await api.post('/financial-approvals/signature', {
          employee_code: employeeCode,
          signature_base64: reader.result,
        });
        setStageSignatures((prev) => ({ ...prev, [stage]: reader.result }));
        toast.success(`تم حفظ توقيع ${STAGE_LABELS[stage]} بنجاح`);
      } catch (error) {
        toast.error(error.response?.data?.detail || 'تعذر حفظ التوقيع');
      } finally {
        setUploadingStage(null);
      }
    };
    reader.readAsDataURL(file);
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const res = await api.put('/financial-approvals/settings', settings);
      setSettings((prev) => ({ ...prev, ...res.data }));
      toast.success('تم حفظ إعدادات دورة الاعتماد');
      loadStageSignatures(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'تعذر حفظ الإعدادات');
    } finally {
      setSaving(false);
    }
  };

  const toggleAllowedType = (stage, type) => {
    setSettings((prev) => {
      const current = prev[`${stage}_allowed_types`] || [];
      const next = current.includes(type) ? current.filter((t) => t !== type) : [...current, type];
      return { ...prev, [`${stage}_allowed_types`]: next };
    });
  };

  const viewRequestDetail = async (requestId) => {
    try {
      const res = await api.get(`/financial-requests/${requestId}`);
      setSelectedRequest(res.data);
    } catch (error) {
      toast.error('تعذر تحميل تفاصيل الطلب');
    }
  };

  const enabledCount = STAGE_ORDER.filter((s) => settings[`${s}_enabled`]).length;
  const displayedRequests = approvedOnly ? requests.filter((r) => r.status === 'approved') : requests;

  return (
    <div className="p-6 space-y-6" dir="rtl">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">
          {approvedOnly ? 'أوامر الصرف المعتمدة' : 'الموافقات المالية'}
        </h1>
        <p className="text-gray-500 mt-1">
          {approvedOnly
            ? 'الطلبات المالية التي اكتملت دورة اعتمادها بالكامل - تقوم مقام أمر الصرف الرسمي'
            : 'دورة اعتماد الطلبات المالية: المحاسب ← المدير التنفيذي ← الرئيس'}
        </p>
      </div>

      <Tabs defaultValue={initialTab} className="w-full">
        <TabsList>
          <TabsTrigger value="requests" data-testid="financial-approvals-requests-tab">
            {approvedOnly ? 'أوامر الصرف المعتمدة' : 'الطلبات'}
          </TabsTrigger>
          <TabsTrigger value="settings" data-testid="financial-approvals-settings-tab">التواقيع واعتماد الصلاحيات</TabsTrigger>
        </TabsList>

        {/* ==================== الطلبات ==================== */}
        <TabsContent value="requests" className="space-y-4">
          <Card className="p-4">
            {loading ? (
              <p className="text-gray-500 text-center py-8">جاري التحميل...</p>
            ) : displayedRequests.length === 0 ? (
              <p className="text-gray-500 text-center py-8">
                {approvedOnly ? 'لا توجد أوامر صرف معتمدة بعد' : 'لا توجد طلبات مالية بعد'}
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-gray-500">
                      <th className="text-right py-2 px-2">رقم الطلب</th>
                      <th className="text-right py-2 px-2">الموظف</th>
                      <th className="text-right py-2 px-2">العنوان</th>
                      <th className="text-right py-2 px-2">المبلغ</th>
                      <th className="text-right py-2 px-2">الحالة</th>
                      <th className="text-right py-2 px-2">تاريخ الطلب</th>
                      <th className="text-right py-2 px-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayedRequests.map((r) => (
                      <tr key={r.request_id} className="border-b hover:bg-gray-50">
                        <td className="py-2 px-2 font-mono text-xs">{r.request_id}</td>
                        <td className="py-2 px-2">{r.employee_name}</td>
                        <td className="py-2 px-2">{r.title}</td>
                        <td className="py-2 px-2">{Number(r.amount || 0).toLocaleString()} ر.س</td>
                        <td className="py-2 px-2">
                          <Badge className={STATUS_COLORS[r.status] || ''}>
                            {STATUS_LABELS[r.status] || r.status}
                          </Badge>
                        </td>
                        <td className="py-2 px-2 text-gray-500">{new Date(r.created_at).toLocaleDateString('ar-SA')}</td>
                        <td className="py-2 px-2 flex items-center gap-2">
                          <Button size="sm" variant="outline" onClick={() => viewRequestDetail(r.request_id)}>
                            عرض التفاصيل
                          </Button>
                          {r.status === 'approved' && (
                            <a
                              href={`${BACKEND_URL}/api/financial-requests/${r.request_id}/approval-document`}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex items-center gap-1 text-emerald-700 hover:underline"
                              title="طباعة أمر الصرف"
                            >
                              <FileDown className="w-4 h-4" />
                            </a>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </TabsContent>

        {/* ==================== إعدادات دورة الاعتماد ==================== */}
        <TabsContent value="settings" className="space-y-4">
          <Card className="p-6 space-y-6">
            {enabledCount === 0 && (
              <div className="bg-amber-50 border border-amber-200 text-amber-800 text-sm rounded-lg p-3">
                تنبيه: كل مراحل الاعتماد مستبعدة حاليًا — أي طلب مالي جديد سيُعتمد تلقائيًا بدون أي مراجعة.
              </div>
            )}

            {STAGE_ORDER.map((stage) => (
              <div key={stage} className="border rounded-lg p-4 space-y-4">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <Switch
                      checked={!!settings[`${stage}_enabled`]}
                      onCheckedChange={(checked) =>
                        setSettings((prev) => ({ ...prev, [`${stage}_enabled`]: checked }))
                      }
                      data-testid={`stage-toggle-${stage}`}
                    />
                    <div>
                      <p className="font-semibold">مرحلة {STAGE_LABELS[stage]}</p>
                      <p className="text-xs text-gray-500">
                        {settings[`${stage}_enabled`] ? 'مفعّلة - يجب اعتماد هذه المرحلة ضمن الدورة' : 'مستبعدة - سيتم تخطي هذه المرحلة بالكامل'}
                      </p>
                    </div>
                  </div>

                  <div className="w-64">
                    <Label className="text-xs text-gray-500 mb-1 block">الموظف المسؤول</Label>
                    <Select
                      value={settings[`${stage}_employee_code`] || ''}
                      onValueChange={(value) =>
                        setSettings((prev) => ({ ...prev, [`${stage}_employee_code`]: value }))
                      }
                      disabled={!settings[`${stage}_enabled`]}
                    >
                      <SelectTrigger data-testid={`stage-employee-select-${stage}`}>
                        <SelectValue placeholder="اختر موظف" />
                      </SelectTrigger>
                      <SelectContent>
                        {employees.map((emp) => (
                          <SelectItem key={emp.employee_code} value={emp.employee_code}>
                            {emp.employee_name_ar} ({emp.employee_code})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className={settings[`${stage}_enabled`] ? '' : 'opacity-50 pointer-events-none'}>
                  <Label className="text-xs text-gray-500 mb-2 block">
                    أنواع الطلبات المالية المسموح لـ{STAGE_LABELS[stage]} اعتمادها
                  </Label>
                  <div className="flex flex-wrap gap-4">
                    {REQUEST_TYPES.map((type) => (
                      <label key={type} className="flex items-center gap-2 text-sm cursor-pointer">
                        <Checkbox
                          checked={(settings[`${stage}_allowed_types`] || []).includes(type)}
                          onCheckedChange={() => toggleAllowedType(stage, type)}
                          data-testid={`stage-type-${stage}-${type}`}
                        />
                        {type}
                      </label>
                    ))}
                  </div>
                </div>

                <div className={`flex items-center gap-4 border-t pt-3 ${settings[`${stage}_enabled`] ? '' : 'opacity-50 pointer-events-none'}`}>
                  <div>
                    <Label className="text-xs text-gray-500 mb-1 block">توقيع {STAGE_LABELS[stage]}</Label>
                    {stageSignatures[stage] ? (
                      <img
                        src={stageSignatures[stage]}
                        alt={`توقيع ${STAGE_LABELS[stage]}`}
                        className="h-14 w-32 object-contain border rounded bg-white"
                      />
                    ) : (
                      <div className="h-14 w-32 border rounded bg-gray-50 flex items-center justify-center text-xs text-gray-400">
                        لا يوجد توقيع
                      </div>
                    )}
                  </div>
                  <div>
                    <input
                      type="file"
                      accept="image/*"
                      disabled={uploadingStage === stage}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleSignatureUpload(stage, file);
                        e.target.value = '';
                      }}
                      className="text-sm"
                      data-testid={`stage-signature-upload-${stage}`}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      ارفع صورة توقيع {STAGE_LABELS[stage]} — تظهر تلقائيًا على مسير الرواتب وأمر الصرف وأي طلب مالي يعتمده.
                    </p>
                  </div>
                </div>
              </div>
            ))}

            <Button onClick={saveSettings} disabled={saving} className="gap-2">
              <Save className="w-4 h-4" />
              {saving ? 'جاري الحفظ...' : 'حفظ الإعدادات'}
            </Button>
          </Card>
        </TabsContent>
      </Tabs>

      {/* ==================== تفاصيل الطلب ==================== */}
      <Dialog open={!!selectedRequest} onOpenChange={(open) => !open && setSelectedRequest(null)}>
        <DialogContent className="max-w-2xl" dir="rtl">
          <DialogHeader>
            <DialogTitle>تفاصيل الطلب المالي {selectedRequest?.request_id}</DialogTitle>
          </DialogHeader>
          {selectedRequest && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-gray-500">الموظف:</span> {selectedRequest.employee_name}</div>
                <div><span className="text-gray-500">النوع:</span> {selectedRequest.request_type}</div>
                <div><span className="text-gray-500">المبلغ:</span> {Number(selectedRequest.amount || 0).toLocaleString()} ر.س</div>
                <div>
                  <span className="text-gray-500">الحالة:</span>{' '}
                  <Badge className={STATUS_COLORS[selectedRequest.status] || ''}>
                    {STATUS_LABELS[selectedRequest.status] || selectedRequest.status}
                  </Badge>
                </div>
              </div>
              <div>
                <p className="text-gray-500 text-sm mb-1">العنوان</p>
                <p className="font-medium">{selectedRequest.title}</p>
              </div>
              {selectedRequest.description && (
                <div>
                  <p className="text-gray-500 text-sm mb-1">الوصف</p>
                  <p>{selectedRequest.description}</p>
                </div>
              )}
              {selectedRequest.attachment_filename && (
                <a
                  href={`${BACKEND_URL}/api/financial-requests/${selectedRequest.request_id}/attachment`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 text-emerald-700 hover:underline text-sm"
                >
                  <Paperclip className="w-4 h-4" /> عرض الملف المرفق ({selectedRequest.attachment_filename})
                </a>
              )}

              {(selectedRequest.status === 'approved' || selectedRequest.status === 'rejected') && (
                <a
                  href={`${BACKEND_URL}/api/financial-requests/${selectedRequest.request_id}/approval-document`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 text-emerald-700 hover:underline text-sm ms-4"
                >
                  <FileDown className="w-4 h-4" /> تحميل مستند الاعتماد الرسمي (بالتواقيع)
                </a>
              )}

              <div className="border-t pt-4">
                <p className="font-semibold mb-2">مراحل الاعتماد</p>
                <div className="space-y-2">
                  {(selectedRequest.stages || []).filter((s) => s.enabled).map((s) => (
                    <div key={s.stage} className="flex items-start gap-3 border rounded-lg p-3">
                      {s.decision === 'approved' && <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />}
                      {s.decision === 'rejected' && <XCircle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />}
                      {!s.decision && <Clock className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />}
                      <div className="flex-1">
                        <p className="font-medium">{STAGE_LABELS[s.stage]}</p>
                        {s.approver_name && <p className="text-xs text-gray-500">{s.approver_name}</p>}
                        {s.notes && <p className="text-sm text-gray-700 mt-1">ملاحظة: {s.notes}</p>}
                        {s.decided_at && (
                          <p className="text-xs text-gray-400 mt-1">{new Date(s.decided_at).toLocaleString('ar-SA')}</p>
                        )}
                      </div>
                      {s.signature_base64 && (
                        <img
                          src={s.signature_base64}
                          alt={`توقيع ${s.approver_name || STAGE_LABELS[s.stage]}`}
                          className="h-12 w-24 object-contain border rounded bg-white"
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
